// @ts-check
import { useCallback, useEffect } from 'react';
import { isMobileUser } from '@/components/routing/UserRoutes/AcceptedRoles';
import Chat from './chat/Chat';
import PermissionsModal from '@/components/modals/permissions/PermissionsModal';
import { useEmergencyProvider } from '@/context_providers/emergency_context/EmergencyProvider';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import { Box, IconButton, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import { t } from 'i18next';
import EmergencyListModal from './ers/EmergencyListModal';
import { useLoading } from '@/context_providers/loading_context/LoadingContext';
import { useEmergencyChatProvider } from '@/context_providers/emergency_chat_context/EmergencyChatProvider';
import {
  useUserContext,
  useEntitlements,
} from '@/context_providers/user_context/UserProvider';
import mapIcon from '@/resources/map_style/image_map_marker.png';
import UserDashboard from './user_dashboard/UserDashboard';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// Emergency store imports
import {
  useShowEmergencyChat,
  useEmergencyData,
  usePreviousEmergencyLocation,
  useShowEmergencyListModal,
  useEmergencyActions,
} from '@/stores/map/emergencyStore';

// Location sharing store imports
import {
  useLocationSharingEnabled,
  useVisibleGroups,
  useShowLocationSharingPanel,
  useLocationMarkers,
  useSelectedLocationMarker,
  useLocationSharingActions,
} from '@/stores/map/locationSharingStore';

// Map UI store imports
import {
  useShowPermissionsModal,
  useLostConnectionInfo,
  useMapCenter,
  useMapZoom,
  useMapInstance,
  useMapUiActions,
} from '@/stores/map/mapUiStore';
import { useDashboardActions } from '@/stores/map/dashboardStore';

import LocationSharingPanel from './location_sharing/LocationSharingPanel';
import LocationSharingGroupSelectionModal from './location_sharing/LocationSharingGroupSelectionModal';
import LocationSharingMarker from './location_sharing/LocationSharingMarker';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import ConnectionLossMarker from './location_sharing/ConnectionLossMarker';
import {
  useTotalDisconnectedUsers,
  useAllGroupUserStatuses,
  setGroupUserStatuses,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import { getAllGroupUserStatusesFromOrg } from '@/repository/UserStatusDAO';
import LostConnectionListModal from './lost_connection/LostConnectionListModal';

import LostConnectionInfoWindow from './lost_connection/LostConnectionInfoWindow';
import EntitlementLockOverlay from '@/components/entitlements/EntitlementLockOverlay';
import { StringKeys } from '@/resources/strings/StringKeys';
import DisconnectedUserButton from './map/DisconnectedUserButton';
import EmergencyListButton from './map/EmergencyListButton';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  APIProvider,
  CollisionBehavior,
  useApiIsLoaded,
} from '@vis.gl/react-google-maps';
import { Map } from '@vis.gl/react-google-maps';
import LocationSharingDrawer from './location_sharing/LocationSharingDrawer';
import UnauthorizedBanner from './map/UnauthorizedBanner';
import { Emergency } from '@/models/Emergency/Emergency';
import { EmergencyMessage } from '@/models/Emergency/EmergencyMessage';

const initialCenter = { lat: 39.8283, lng: -98.5795 }; // Approximate center of the US

function SafeguardMap() {
  const mapKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_API_ID;
  const initialZoom = 3;

  // Store state
  const showEmergencyChat = useShowEmergencyChat();
  const emergencyData = useEmergencyData();
  const previousEmergencyLocation = usePreviousEmergencyLocation();
  const showEmergencyListModal = useShowEmergencyListModal();

  const locationSharingEnabled = useLocationSharingEnabled();
  const visibleGroups = useVisibleGroups();
  const showLocationSharingPanel = useShowLocationSharingPanel();
  const locationMarkers = useLocationMarkers();
  const selectedLocationMarker = useSelectedLocationMarker();

  const showPermissionsModal = useShowPermissionsModal();
  const lostConnectionInfo = useLostConnectionInfo();
  const mapCenter = useMapCenter();
  const mapZoom = useMapZoom();
  const mapInstance = useMapInstance();

  // Store actions
  const emergencyActions = useEmergencyActions();
  const locationSharingActions = useLocationSharingActions();
  const mapUiActions = useMapUiActions();
  const dashboardActions = useDashboardActions();

  const {
    initializeLocationSharingFromStorage,
    initializeVisibleGroupsFromStorage,
    updateLocationMarkers,
  } = locationSharingActions;

  // Initialize store state from localStorage on mount
  useEffect(() => {
    initializeLocationSharingFromStorage();
    initializeVisibleGroupsFromStorage();
  }, [
    initializeLocationSharingFromStorage,
    initializeVisibleGroupsFromStorage,
  ]);

  const { setPageLoading, normalUserSignalRStatus, transitionComplete } =
    useLoading();

  const {
    setSelectedEmergency,
    selectedEmergency,
    setSelectedEmergencyMessages,
  } = useEmergencyChatProvider();
  const isMapLoaded = useApiIsLoaded();

  const { bearerToken } = useUserContext();
  const entitlements = useEntitlements();
  const { emergencies } = useEmergencyProvider();
  const { orgUser, userRole } = useUserUpdaterProvider();
  const { groups, pvdConfigurations, organization } =
    useOrganizationUpdaterProvider();
  const numDisconnectedUsers = useTotalDisconnectedUsers();
  const allGroupUserStatuses = useAllGroupUserStatuses();

  useEffect(() => {
    updateLocationMarkers(allGroupUserStatuses, emergencyData, groups);
  }, [
    isMapLoaded,
    mapInstance,
    visibleGroups,
    updateLocationMarkers,
    locationSharingEnabled,
    allGroupUserStatuses,
    emergencyData,
    groups,
  ]);

  /**
   * Do initial load of group user statuses when the component mounts and bearerToken is available
   */
  useEffect(() => {
    const loadGroupUserStatuses = async () => {
      if (isMobileUser(userRole)) return;
      if (!bearerToken) return;

      try {
        const groupUserStatusesArray = await getAllGroupUserStatusesFromOrg(
          organization?.org_code,
        );

        // Populate the store with each group's data
        groupUserStatusesArray.forEach((groupUserStatuses) => {
          setGroupUserStatuses(groupUserStatuses.group_name, groupUserStatuses);
        });
      } catch (error) {
        console.error('Failed to load group user statuses:', error);
      }
    };

    loadGroupUserStatuses();
  }, [bearerToken, organization?.org_code, userRole]);

  const getUserLocation = () => {
    return new Promise((resolve, _) => {
      if (!navigator.geolocation) {
        resolve({ lat: 39.8283, lng: -98.5795 });
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (_) => {
            resolve({ lat: 39.8283, lng: -98.5795 });
          },
        );
      }
    });
  };

  const initializeMap = useCallback(
    async (isInitialLoad = false) => {
      try {
        const userLocation = await getUserLocation();
        const hasLocation =
          userLocation.lat !== 39.8283 || userLocation.lng !== -98.5795;

        const targetCenter = hasLocation ? userLocation : initialCenter;
        const targetZoom = hasLocation ? 12 : initialZoom;

        mapUiActions.setMapCenter(targetCenter);
        mapUiActions.setMapZoom(targetZoom);

        if (mapInstance) {
          mapInstance.panTo(targetCenter);
          mapInstance.setZoom(targetZoom);
        }

        if (isInitialLoad) {
          setPageLoading(false);
        }
      } catch (error) {
        console.error('Error getting user location:', error);
        mapUiActions.setMapCenter(initialCenter);
        mapUiActions.setMapZoom(initialZoom);
        if (isInitialLoad) {
          setPageLoading(false);
        }
      }
    },
    [mapInstance, mapUiActions, setPageLoading, initialZoom],
  );

  // Initial map setup
  useEffect(() => {
    initializeMap(true);
  }, [initializeMap]);

  // Handle emergency updates
  useEffect(() => {
    if (emergencyData) {
      const activeEmergencies = Object.values(emergencyData).filter((element) =>
        element.emergency?.isActiveEmergency(),
      );
      if (activeEmergencies.length > 0) {
        const latestEmergency = activeEmergencies[activeEmergencies.length - 1];
        const latestLocation = latestEmergency.locations?.[0];
        if (
          latestLocation &&
          (!previousEmergencyLocation ||
            previousEmergencyLocation.latitude !== latestLocation.latitude ||
            previousEmergencyLocation.longitude !== latestLocation.longitude)
        ) {
          mapUiActions.setMapCenter({
            lat: latestLocation.latitude,
            lng: latestLocation.longitude,
          });
          mapUiActions.setMapZoom(15);
          emergencyActions.setPreviousEmergencyLocation(latestLocation);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emergencyData]);

  useEffect(() => {
    if (emergencies) {
      /**
       * @type {Record<string,
       * {emergency: Emergency | null;
       * messages: EmergencyMessage[];
       * locations: import('@/models/User/UserLocation').UserLocation[]
       * }>
       * }
       */
      Object.entries(emergencies).forEach(([key, emergencyGroup]) => {
        if (selectedEmergency != null) {
          if (selectedEmergency.emergency?.id === key) {
            setSelectedEmergency(emergencyGroup);
            setSelectedEmergencyMessages(emergencyGroup.messages);
          }
        }
      });
      emergencyActions.setEmergencyData(emergencies);
    } else {
      // signal to close the modal
      emergencyActions.setShowEmergencyListModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emergencies]);

  useEffect(() => {
    if (
      mapInstance &&
      mapCenter.lat !== 39.8283 &&
      mapCenter.lng !== -98.5795
    ) {
      mapInstance.panTo(mapCenter);
      // Use the zoom level from the store instead of hardcoding it
      mapInstance.setZoom(mapZoom);
    }
  }, [mapInstance, mapCenter, mapZoom]);

  useEffect(() => {
    return () => {
      mapUiActions.setSelectedUser?.(null);
      locationSharingActions.setSelectedLocationMarker?.(null);
      dashboardActions.closeDashboard();
    };
    // only run on unmount, ignore dependencies that would cause this to re-run unnecessarily
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {organization &&
      orgUser &&
      normalUserSignalRStatus === 'Connected' &&
      transitionComplete ? (
        <>
          {/* Main container for map and panel */}
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              // Flexbox gives the overlay's root div a definite height so the
              // height:100% Box beneath it resolves correctly (a plain block
              // child with no explicit height would otherwise collapse).
              '& > *': { flex: 1, minHeight: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Location Sharing Panel - Desktop version (sm screens and above).
                  Kept outside the entitlement lock so admins can still manage group
                  visibility while the org's paid plan/trial is pending — only the
                  map canvas itself (and its markers) is gated below. */}
              {locationSharingEnabled && showLocationSharingPanel && (
                <Box
                  sx={{
                    width: '280px',
                    maxWidth: '280px',
                    height: '100%',
                    overflow: 'auto',
                    position: 'relative',
                    zIndex: 2,
                    display: { xs: 'none', sm: 'block' }, // Hide on xs (extra small) screens, show on sm (small) screens and above
                  }}
                >
                  <LocationSharingPanel />
                </Box>
              )}

              {/* Location Sharing Panel - Mobile version (Bottom Drawer for xs screens) */}
              {locationSharingEnabled && showLocationSharingPanel && (
                <LocationSharingDrawer />
              )}

              <Box sx={{ flex: 1, height: '100%', position: 'relative' }}>
                <EntitlementLockOverlay
                  locked={!entitlements.orgHasAnyPaidInventory}
                  description={t(StringKeys.MAP_REQUIRES_PAID_PLAN)}
                >
                  {/* Map Container */}
                  <Box
                    sx={{
                      width: '100%',
                      height: {
                        xs: 'calc(100% - 56px)', // On xs, subtract height of LocationSharingDrawer
                        sm: '100%',
                      },
                    }}
                  >
                    <APIProvider apiKey={mapKey}>
                      <div
                        style={{ height: '100%', width: '100%' }}
                        className="map-container"
                      >
                        <Map
                          mapId={mapId}
                          fullscreenControl={false}
                          // styles={mapStyle} using cloud map styles instead
                          defaultZoom={mapZoom}
                          defaultCenter={mapCenter}
                          disableDefaultUI={false}
                          onIdle={(e) => {
                            // e.map is the google.maps.Map instance
                            mapUiActions.setMapInstance(e.map);
                          }}
                        >
                          {/* ERSMarker only for emergency creator, LocationSharingMarker for everyone else */}
                          {locationMarkers.map((marker, _) => {
                            const uniqueKey = `${marker.group_name || 'no-group'}-${marker.user_auth_id}`;
                            const activeEmergencies = Object.values(
                              emergencyData || {},
                            ).filter((element) =>
                              element.emergency?.isActiveEmergency(),
                            );
                            const emergencyCreatorPositions = activeEmergencies
                              .map((emergency) => {
                                const creatorMarker = locationMarkers.find(
                                  (m) =>
                                    m.user_auth_id ===
                                    emergency.emergency?.creator_auth_id,
                                );
                                return creatorMarker?.position;
                              })
                              .filter(Boolean);

                            if (marker.isEmergencyCreator) {
                              const emergency = Object.values(
                                emergencyData || {},
                              ).find(
                                (element) =>
                                  element.emergency?.isActiveEmergency() &&
                                  element.emergency.creator_auth_id ===
                                    marker.user_auth_id,
                              );

                              return (
                                <AdvancedMarker
                                  key={uniqueKey}
                                  position={marker.position}
                                  title={
                                    emergency
                                      ? emergency.emergency?.incident
                                      : 'No emergency'
                                  }
                                  collisionBehavior={CollisionBehavior.REQUIRED}
                                  zIndex={20}
                                  style={{ cursor: 'pointer' }}
                                  anchorPoint={
                                    AdvancedMarkerAnchorPoint.BOTTOM_CENTER
                                  }
                                  onClick={() => {
                                    setSelectedEmergency(emergency);
                                    emergencyActions.setShowEmergencyChat(true);
                                  }}
                                >
                                  {/* Use your image/icon as real HTML so you can style it */}
                                  <img
                                    src={mapIcon}
                                    alt=""
                                    style={{
                                      pointerEvents: 'none',
                                      cursor: 'pointer',
                                    }}
                                  />
                                </AdvancedMarker>
                              );
                            } else if (marker.hasLostConnection) {
                              return (
                                <ConnectionLossMarker
                                  groupName={marker.group_name ?? ''}
                                  key={uniqueKey}
                                  position={marker.position}
                                  userAuthId={marker.user_auth_id}
                                  lastUpdated={marker.last_updated}
                                  isSelected={
                                    selectedLocationMarker?.user_auth_id ===
                                    marker.user_auth_id
                                  }
                                />
                              );
                            } else if (marker.isEmergencyResponder) {
                              return (
                                <LocationSharingMarker
                                  groupName={marker.group_name}
                                  key={uniqueKey}
                                  position={marker.position}
                                  userAuthId={marker.user_auth_id}
                                  isEmergencyResponder={true}
                                  emergencyCreatorPositions={
                                    emergencyCreatorPositions
                                  }
                                />
                              );
                            } else if (
                              locationSharingEnabled &&
                              marker.group_name &&
                              visibleGroups.includes(marker.group_name)
                            ) {
                              return (
                                <LocationSharingMarker
                                  key={uniqueKey}
                                  groupName={marker.group_name}
                                  position={marker.position}
                                  userAuthId={marker.user_auth_id}
                                  isEmergencyResponder={false}
                                  emergencyCreatorPositions={
                                    emergencyCreatorPositions
                                  }
                                />
                              );
                            }
                            return null;
                          })}

                          {/* Controls Container - Group buttons in one area for better positioning */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px',
                              zIndex: 5,
                            }}
                          >
                            <IconButton
                              onClick={() => initializeMap(false)}
                              sx={{
                                backgroundColor: 'white',
                                boxShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                                '&:hover': { backgroundColor: '#f0f0f0' },
                                width: '40px',
                                height: '40px',
                                borderRadius: '2px',
                              }}
                              aria-label="Show your location"
                            >
                              <MyLocationIcon />
                            </IconButton>
                            {/* Emergency list button - shown when there are active emergencies; replaced with a disabled lock icon when the org lacks Respond inventory */}
                            {Object.values(emergencyData).some((emergency) =>
                              emergency.emergency?.isActiveEmergency(),
                            ) &&
                              (entitlements.orgHasRespondInventory ? (
                                <EmergencyListButton />
                              ) : (
                                <Tooltip
                                  title={t(
                                    StringKeys.EMERGENCY_MAP_REQUIRES_RESPOND,
                                  )}
                                >
                                  <span>
                                    <IconButton
                                      disabled
                                      aria-label={t(
                                        StringKeys.EMERGENCY_MAP_REQUIRES_RESPOND,
                                      )}
                                      sx={{
                                        backgroundColor: 'white',
                                        boxShadow:
                                          '0px 2px 4px rgba(0,0,0,0.3)',
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '2px',
                                      }}
                                    >
                                      <LockIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ))}
                            {numDisconnectedUsers > 0 && (
                              <DisconnectedUserButton
                                numDisconnectedUsers={numDisconnectedUsers}
                              />
                            )}
                          </Box>
                        </Map>
                      </div>
                    </APIProvider>
                  </Box>
                </EntitlementLockOverlay>
              </Box>
            </Box>
          </Box>
          <UserDashboard
            groups={groups}
            pvdConfigurations={pvdConfigurations}
            organization={organization}
            allGroupUserStatuses={allGroupUserStatuses}
          />
          {!userRole?.match(/Admin|Dispatch/) ? <UnauthorizedBanner /> : null}

          <Chat
            show={entitlements.orgHasRespondInventory && showEmergencyChat}
            onHide={() => {
              setSelectedEmergency(null);
              emergencyActions.setShowEmergencyChat(false);
            }}
          />
          <PermissionsModal
            show={showPermissionsModal}
            onHide={() => mapUiActions.setShowPermissionsModal(false)}
          />
          <EmergencyListModal
            show={entitlements.orgHasRespondInventory && showEmergencyListModal}
            emergencies={emergencyData}
            onHide={() => emergencyActions.setShowEmergencyListModal(false)}
            setSelectedEmergency={setSelectedEmergency}
            setShowEmergencyChat={emergencyActions.setShowEmergencyChat}
          />
          <LocationSharingGroupSelectionModal />
          <LostConnectionListModal />
          <LostConnectionInfoWindow
            isOpen={lostConnectionInfo?.isVisible ?? false}
            updateLostConnectionInfo={mapUiActions.setLostConnectionInfo}
            userAuthId={lostConnectionInfo?.userAuthId}
            lastUpdated={
              locationMarkers?.find(
                (m) => m.user_auth_id === lostConnectionInfo?.userAuthId,
              )?.last_updated
            }
          />
        </>
      ) : null}
    </>
  );
}

export default SafeguardMap;
